<template>
  <div>
    <custom-alert
      v-if="editable"
      class="mr-35"
      items-center
      icon-name="InfoAlt"
    >
      To overwrite a default header add a new header with the same key.
    </custom-alert>
    <b-table
      ref="table"
      class="headers-table mb-0"
      :items="headers"
      :fields="fields"
      select-mode="multi"
      show-empty
      bordered
      responsive
    >
      <template #cell(state)="data">
        <a
          :class="data.item.isDefault || !editable ? 'header-default' : ''"
          @click="
            data.item.isDefault || !editable ? '' : toggleState(data.item)
          "
        >
          <select-button
            :selected="data.item.state"
            :disabled="data.item.isDefault || !editable"
          />
        </a>
      </template>

      <template
        #cell(key)="data"
        class="header-key-field"
      >
        <validation-provider
          #default="{ errors }"
          name="key"
          tag="div"
          rules="alpha-dash"
          class="header-key-field mr-1 w-100"
        >
          <b-form-input
            v-model.trim="data.item.key"
            placeholder="Key"
            class="m-0"
            :class="!data.item.valid ? 'key-duplicated' : ''"
            :disabled="!data.item.state || data.item.isDefault || !editable"
            :state="errors.length > 0 ? false : null"
            @input="changeKeys"
            @keydown.space.prevent
            @change="$emit('headersUpdated')"
          />
        </validation-provider>
      </template>
      <template
        #cell(value)="data"
        class="header-key-field"
      >
        <validation-provider
          #default="{ errors }"
          name="value"
          tag="div"
          class="header-key-field mr-1 w-100"
        >
          <b-form-input
            v-if="data.item.isDefault"
            :value="
              data.item.value === null
                ? '<calculated when request is sent>'
                : data.item.value
            "
            type="text"
            class="m-0"
            :state="errors.length > 0 ? false : null"
            :class="!data.item.valid ? 'key-duplicated' : ''"
            :disabled="!data.item.state || data.item.isDefault || !editable"
            placeholder="Value"
          />
          <b-form-input
            v-if="!data.item.isDefault"
            v-model="data.item.value"
            type="text"
            class="m-0"
            :state="errors.length > 0 ? false : null"
            :class="!data.item.valid ? 'key-duplicated' : ''"
            :disabled="!data.item.state || data.item.isDefault || !editable"
            placeholder="Value"
            @change="$emit('headersUpdated')"
          />
        </validation-provider>
      </template>
      <template #cell(delete)="data">
        <div
          :class="
            data.item.isDefault || !editable ? 'delete-default-header' : ''
          "
          class="delete-header"
          @click="deleteHeader(data.item)"
        >
          <GjIcon name="Delete" />
        </div>
      </template>
      <template #empty>
        <div />
      </template>
    </b-table>
    <div
      v-if="editable"
      class="add-header-button"
    >
      <a
        class="text-primary text-thick"
        @click="addHeader()"
      >
        + Add new header
      </a>
    </div>
  </div>
</template>
<script>
import { ValidationProvider } from 'vee-validate'
import { alphaDash } from '@validations'

export default {
  name: 'FormHeaders',
  components: {
    ValidationProvider,
  },
  props: {
    healthcheck: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      alphaDash,
      headersKeys: [],
      fields: [
        {
          key: 'state',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'key', isPrimary: true, label: 'Key' },
        { key: 'value', isPrimary: true, label: 'Value' },
        this.editable
          ? {
            key: 'delete',
            isPrimary: true,
            variant: 'select',
            hide: true,
            label: '',
          }
          : {},
      ],
    }
  },
  created() {
    this.pushHeaders()
    this.headers.sort((x, y) => y.isDefault - x.isDefault)
  },
  mounted() {},
  methods: {
    toggleState(item) {
      item.state = !item.state
    },
    changeKeys() {
      this.headersKeys = []
      this.headers.forEach(e => {
        this.headersKeys.push(e.key?.toLowerCase() || '')
      })
      this.isKeyDuplicated()
    },
    addHeader() {
      this.headers.push({
        key: null,
        value: null,
        state: true,
        isDefault: false,
        valid: true,
      })
    },
    pushHeaders() {
      if (
        this.healthcheck.headers === undefined
        || this.healthcheck.headers === null
        || Object.keys(this.healthcheck.headers).length === 0
      ) {
        this.addHeader()
        return
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const o in this.healthcheck.headers) {
        if (this.healthcheck.headers[o]) {
          this.$emit('pushHeaders', {
            key: o,
            value: this.healthcheck.headers[o].value,
            state: this.healthcheck.headers[o].state,
            isDefault: this.healthcheck.headers[o].isDefault,
            valid: true,
          })
        }
      }
    },
    deleteHeader(item) {
      if (!item.isDefault && this.editable) {
        this.$emit('deleteHeader', item)
        this.changeKeys()
      }
    },
    isKeyDuplicated() {
      this.headers.forEach((e, index) => {
        e.valid = true
        if (e.key && index !== this.headersKeys.lastIndexOf(e.key.toLowerCase())) e.valid = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.add-header-button {
  margin-top: 8px;
  text-align: center;
}
.key-duplicated {
  text-decoration: line-through;
}
.header-default {
  div {
    opacity: 0.7;
    cursor: default;
  }
}
.delete-header {
  opacity: 0.9;
  cursor: pointer;
}
.headers-table::v-deep {
  .delete-default-header {
    opacity: 0.7;
    cursor: default;
  }
  .header-key-field {
    .is-invalid {
      padding-right: 0;
    }
  }
  td {
    padding: 8px;
    &.table-select {
      padding: 8px 16px;
    }
  }
  .b-table-empty-row {
    td {
      padding: 0;
    }
  }
}
</style>
